/**
 * Browser entrypoint — wraps posthog-js.
 *
 * BROWSER ONLY. Never import this from Node (it would pull posthog-js into a
 * server bundle); use `@tupai/analytics/server` there.
 *
 * Ground rule 1: analytics is never load-bearing. Every function here is
 * fire-and-forget, swallows its own errors, and is a complete no-op when no
 * PostHog key is configured.
 */

import posthog from 'posthog-js';
import type { CaptureResult, PostHogConfig } from 'posthog-js';

import type { EventName, EventProps } from './events';
import type { AppName, AutoProperties, Feature, Surface } from './properties';
import { DEFAULT_POSTHOG_HOST, URL_SHAPED_KEYS, routeToFeature, sanitizeUrl } from './properties';

export * from './identity';
export * from './first-touch';

export interface InitAnalyticsOptions {
  app: AppName;
  /** Public PostHog project key. Missing/empty ⇒ analytics is fully disabled. */
  key?: string;
  host?: string;
  surface?: Surface;
  /** Usually `import.meta.env.VITE_APP_VERSION`. */
  appVersion?: string;
}

// Re-exported so existing `from './client'` importers keep working; the
// definition lives in ./properties (no SDK import) so server.ts can share it.
export { DEFAULT_POSTHOG_HOST };

interface AnalyticsState {
  enabled: boolean;
  app: AppName;
  surface: Surface;
  appVersion: string;
  feature?: Feature;
  /**
   * The current route TEMPLATE (e.g. `/learn/lesson`), set on every
   * navigation next to `feature`. `before_send` overwrites every event's
   * `$current_url` with this (§6.3.1 defect 2) — never a raw href.
   */
  route?: string;
  daysSinceSignup?: number;
  isFirstSession?: boolean;
}

const state: AnalyticsState = {
  enabled: false,
  app: 'student',
  surface: 'web',
  appVersion: '',
};

/**
 * `before_send` — one hook, four defects (docs/plans/analytics-implementation-plan.md §6.3.1):
 *
 * 1. `$current_url` (and its friends) are built by posthog-js from
 *    `location.href` on EVERY event, including autocapture — and this app
 *    puts a base64 worksheet blob in the query string (`?w=`). Dropping the
 *    key is not an option — it is one we need; the problem is entirely in the
 *    value. Fixed by overwriting `$current_url` with the tracked route
 *    template, or — if no route is known yet — stripping the query/hash
 *    instead of leaving a raw href.
 * 2. Our `$pageview` and posthog's own events used to disagree about
 *    `$current_url` (route template vs. resolved href). This hook makes
 *    every event, from every source, agree.
 * 3. `$referrer`, `$initial_current_url`, `$initial_referrer`, `$pathname`
 *    and `$initial_pathname` are URL-shaped too and get the same query/hash
 *    strip via `sanitizeUrl`.
 * 4. Replaces the deprecated `sanitize_properties` (still runs in 1.415.1,
 *    but its call site logs an error on every single event).
 *
 * Never throws into posthog-js (ground rule 1) — any failure returns the
 * event unmodified rather than dropping or breaking capture.
 */
function beforeSend(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event;
  try {
    const properties: Record<string, unknown> = { ...event.properties };

    if ('$current_url' in properties) {
      properties.$current_url = state.route ?? sanitizeUrl(properties.$current_url);
    }
    for (const key of URL_SHAPED_KEYS) {
      if (key in properties) properties[key] = sanitizeUrl(properties[key]);
    }

    // `$set` / `$set_once` are SIBLINGS of `properties` on CaptureResult, not
    // nested inside it, so the sanitising above does not reach them. They must
    // be handled explicitly for two reasons:
    //   1. `$set_once` carries posthog's `$initial_*` person properties —
    //      including `$initial_current_url`, a raw href. Set-once means it
    //      sticks to the person permanently, so a leak here never expires.
    //   2. The deprecated `sanitize_properties` this replaced was invoked
    //      separately for `$set_once`; not doing the same would be a silent
    //      regression rather than a like-for-like migration.
    const sanitizePersonProps = (props: Record<string, unknown> | undefined) => {
      if (!props) return props;
      const out: Record<string, unknown> = { ...props };
      for (const key of Object.keys(out)) {
        if (key === '$current_url' || URL_SHAPED_KEYS.includes(key) || /_url$/.test(key)) {
          out[key] = sanitizeUrl(out[key]);
        }
      }
      return out;
    };

    return {
      ...event,
      properties,
      ...(event.$set ? { $set: sanitizePersonProps(event.$set) } : {}),
      ...(event.$set_once ? { $set_once: sanitizePersonProps(event.$set_once) } : {}),
    } as CaptureResult;
  } catch (err) {
    console.warn('[analytics] before_send failed, sending event unmodified:', err);
    return event;
  }
}

/**
 * The posthog-js config from docs/plans/analytics-implementation-plan.md §6.3 / §6.3.1.
 *
 * Exported so `client.test.ts` can assert on the config.
 */
export function buildPosthogConfig(host: string = DEFAULT_POSTHOG_HOST): Partial<PostHogConfig> {
  return {
    api_host: host,
    autocapture: true,
    // Driven from the router instead — on native, hash history makes
    // location.pathname `/index.html` for every screen (§6.1(a)).
    capture_pageview: false,
    // MUST be explicit: capture_pageleave defaults to `'if_capture_pageview'`,
    // gated as `capture_pageleave === true || ('if_capture_pageview' ===
    // config.capture_pageleave && capture_pageview)`. Since capture_pageview
    // is false above, that default would silently turn $pageleave off
    // entirely — no time-on-page, no bounce rate (§6.3.1 defect 3).
    capture_pageleave: true,
    // The Capacitor WebView origin makes cookie-only persistence unreliable
    // and fragments identity across launches (§6.1(b)).
    persistence: 'localStorage+cookie',
    // URL normalisation — replaces the deprecated `sanitize_properties`
    // (§6.3.1 defect 4).
    before_send: beforeSend,
  } as Partial<PostHogConfig>;
}

/**
 * Fire-and-forget init. Mirrors `initBilling()` in `main.tsx`. Resolves even
 * when disabled or when posthog-js throws.
 */
export async function initAnalytics(options: InitAnalyticsOptions): Promise<void> {
  state.app = options.app;
  state.surface = options.surface ?? 'web';
  state.appVersion = options.appVersion ?? '';

  const key = options.key?.trim();
  if (!key) {
    state.enabled = false;
    console.info('[analytics] no PostHog key configured — analytics disabled');
    return;
  }

  try {
    posthog.init(key, buildPosthogConfig(options.host?.trim() || DEFAULT_POSTHOG_HOST));
    state.enabled = true;
  } catch (err) {
    state.enabled = false;
    console.warn('[analytics] init failed:', err);
  }
}

export function isAnalyticsEnabled(): boolean {
  return state.enabled;
}

/**
 * The feature the user is currently in. Set from the router on every
 * navigation so custom events inherit it and segment identically to
 * `$pageview` (§6.3). AMA has no route — call this explicitly with `'ama'`
 * around the AskSheet.
 */
export function setFeature(feature: Feature | undefined): void {
  state.feature = feature;
}

export function getFeature(): Feature | undefined {
  return state.feature;
}

/**
 * The current route TEMPLATE. Set from the router on every navigation
 * alongside `setFeature` — `before_send` overwrites every event's
 * `$current_url` with this (§6.3.1). `capturePageview` also sets it, but
 * router code should call this explicitly first (mirrors `setFeature`) so
 * the value is current even before the `$pageview` capture resolves.
 */
export function setRoute(template: string | undefined): void {
  state.route = template;
}

export function getRoute(): string | undefined {
  return state.route;
}

/**
 * Per-user context that every event carries (§6.2). Set once identity is
 * known; both values are simply omitted until then.
 */
export function setSessionContext(context: {
  daysSinceSignup?: number;
  isFirstSession?: boolean;
}): void {
  if (context.daysSinceSignup !== undefined) state.daysSinceSignup = context.daysSinceSignup;
  if (context.isFirstSession !== undefined) state.isFirstSession = context.isFirstSession;
}

function autoProperties(): AutoProperties {
  return {
    app: state.app,
    surface: state.surface,
    app_version: state.appVersion,
    feature: state.feature,
    days_since_signup: state.daysSinceSignup,
    is_first_session: state.isFirstSession,
  };
}

/**
 * Emit a registered event. An unregistered name, or a registered name with the
 * wrong properties, is a compile error (ground rule 3).
 */
export function capture<E extends EventName>(event: E, props: EventProps<E>): void {
  if (!state.enabled) return;
  try {
    posthog.capture(event, { ...autoProperties(), ...props });
  } catch {
    // never load-bearing
  }
}

/**
 * Manual `$pageview`. `routeId` is the MATCHED ROUTE ID (student) or a
 * `:param` route template (parent) — not `location.pathname`, which is
 * `/index.html` on native and explodes params into thousands of urls.
 *
 * `params` is the pre-filtered, allowlisted nested object from
 * `pickParams()` (§6.3.1) — never the raw search object, which is exactly
 * what would leak `?w=`. Omitted entirely (never `{}`) when empty.
 */
export function capturePageview(args: {
  routeId: string;
  url?: string;
  params?: Record<string, string | number | boolean>;
}): void {
  if (!state.enabled) return;
  try {
    const feature = routeToFeature(state.app, args.routeId);
    if (feature) state.feature = feature;
    state.route = args.routeId;
    posthog.capture('$pageview', {
      ...autoProperties(),
      feature,
      $current_url: args.url ?? args.routeId,
      route_id: args.routeId,
      ...(args.params ? { params: args.params } : {}),
    });
  } catch {
    // never load-bearing
  }
}

/** Escape hatch for feature flags / replay control. May be undefined. */
export function getPosthogClient(): typeof posthog | undefined {
  return state.enabled ? posthog : undefined;
}
